import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Server, Code, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { divisions, stats, testimonials } from '../mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-background to-background -z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block mb-3 md:mb-4 px-3 md:px-4 py-1.5 md:py-2 bg-primary/20 text-primary border border-primary/30 rounded-full text-xs md:text-sm font-semibold animate-fade-in backdrop-blur-sm">
              Excellence in Trading, Consulting & Technology
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-4 md:mb-6 leading-tight px-4">
              ALM Group of <span className="text-primary">Companies</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 leading-relaxed px-4">
              Your trusted partner for international trade, strategic consulting, and cutting-edge technology solutions. 
              Building success across four specialized divisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
              <Link to="/about">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50 shadow-primary/50 hover:shadow-primary/80 transition-all duration-300 group">
                  Discover Our Story
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 shadow-md hover:shadow-lg shadow-primary/50 transition-all duration-300">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>

          {/* Sliding Company Logos Banner */}
          <div className="mt-8 md:mt-12 lg:mt-16 overflow-hidden">
            <div className="flex animate-scroll whitespace-nowrap">
              {[1, 2, 3, 4, 1, 2, 3, 4].map((logoNum, index) => (
                <div
                  key={index}
                  className="inline-flex items-center justify-center mx-2 md:mx-3 px-6 md:px-10 py-4 md:py-6 bg-card rounded-lg shadow-lg border border-border hover:border-primary/50 hover:shadow-primary/20 transition-all duration-300"
                  style={{ minWidth: 'fit-content' }}
                >
                  <img 
                    src={`/slider-logo-${logoNum}.png`}
                    alt={`Division ${logoNum}`}
                    className="h-12 md:h-16 w-auto object-contain"
                    style={{ maxWidth: '200px' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-1 md:mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-muted-foreground font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Image first on mobile */}
            <div className="relative order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1603201667141-5a2d4c673378"
                alt="Team Meeting"
                className="rounded-2xl shadow-2xl border border-border w-full"
              />
              <div className="absolute -bottom-4 md:-bottom-6 -left-4 md:-left-6 bg-gradient-to-br from-primary to-purple-600 text-white p-4 md:p-6 rounded-xl shadow-xl shadow-primary/20 shadow-primary/50">
                <div className="text-2xl md:text-3xl font-bold">15+</div>
                <div className="text-xs md:text-sm">Years of Excellence</div>
              </div>
            </div>
            {/* Content second on mobile */}
            <div className="order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">About ALM Group</h2>
              <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6 leading-relaxed">
                ALM Group of Companies is a premier business conglomerate offering comprehensive solutions across 
                trading, consulting, and technology sectors. With over 15 years of excellence, we've built a reputation 
                for reliability, innovation, and client success.
              </p>
              <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 leading-relaxed">
                Our four specialized divisions work seamlessly to provide end-to-end solutions that drive growth, 
                optimize operations, and transform businesses in the digital age.
              </p>
              <Link to="/about">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50 shadow-primary/50 hover:shadow-primary/80 transition-all duration-300 w-full sm:w-auto">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Divisions Section */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">Our Divisions</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
              Four specialized divisions working together to deliver comprehensive business solutions
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            {divisions.map((division, index) => {
              const icons = [Building2, Users, Server, Code];
              const Icon = icons[index];
              return (
                <Card key={division.id} className="group hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 border-2 hover:border-primary/50 bg-card">
                  <CardContent className="p-0">
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img
                        src={division.image}
                        alt={division.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <div className="w-12 h-12 bg-primary/20 backdrop-blur-sm border border-primary rounded-lg flex items-center justify-center mb-2">
                          <Icon className="text-primary" size={24} />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors mb-2">{division.name}</h3>
                      <p className="text-sm text-primary font-semibold mb-3">{division.tagline}</p>
                      <p className="text-muted-foreground mb-4 line-clamp-3">{division.description}</p>
                      <Link to={`/services/${division.id}`}>
                        <Button className="w-full bg-transparent border-2 border-border text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 group-hover:bg-primary group-hover:text-white group-hover:border-primary">
                          Explore Services
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="md:hidden">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 3500,
                }),
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-2">
                {divisions.map((division, index) => {
                  const icons = [Building2, Users, Server, Code];
                  const Icon = icons[index];
                  return (
                    <CarouselItem key={division.id} className="pl-2 basis-full sm:basis-3/4">
                      <Card className="group hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 border-2 hover:border-primary/50 bg-card">
                        <CardContent className="p-0">
                          <div className="relative h-48 overflow-hidden rounded-t-lg">
                            <img
                              src={division.image}
                              alt={division.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                            <div className="absolute bottom-4 left-4">
                              <div className="w-12 h-12 bg-primary/20 backdrop-blur-sm border border-primary rounded-lg flex items-center justify-center mb-2">
                                <Icon className="text-primary" size={24} />
                              </div>
                            </div>
                          </div>
                          <div className="p-6">
                            <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors mb-2">{division.name}</h3>
                            <p className="text-sm text-primary font-semibold mb-3">{division.tagline}</p>
                            <p className="text-muted-foreground mb-4 line-clamp-3">{division.description}</p>
                            <Link to={`/services/${division.id}`}>
                              <Button className="w-full bg-transparent border-2 border-border text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 group-hover:bg-primary group-hover:text-white group-hover:border-primary">
                                Explore Services
                                <ArrowRight className="ml-2" size={16} />
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-primary/20 via-purple-600/10 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">Why Choose ALM Group?</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
              We deliver excellence through expertise, innovation, and unwavering commitment to client success
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Comprehensive Solutions",
                description: "From trading to technology, we offer end-to-end solutions under one roof"
              },
              {
                title: "Global Expertise",
                description: "15+ years of experience serving clients across 50+ countries worldwide"
              },
              {
                title: "Client-Focused Approach",
                description: "Your success is our mission. We tailor solutions to your unique needs"
              },
              {
                title: "Innovation Driven",
                description: "We leverage the latest technologies and best practices in every project"
              },
              {
                title: "Proven Track Record",
                description: "500+ successful projects and 250+ satisfied clients speak for themselves"
              },
              {
                title: "24/7 Support",
                description: "Our dedicated team is always available to support your business needs"
              }
            ].map((item, index) => (
              <div key={index} className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 hover:bg-card hover:border-primary/50 transition-all duration-300">
                <CheckCircle className="text-primary mb-4" size={32} />
                <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="md:hidden">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 3500,
                }),
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-2">
                {[
                  {
                    title: "Comprehensive Solutions",
                    description: "From trading to technology, we offer end-to-end solutions under one roof"
                  },
                  {
                    title: "Global Expertise",
                    description: "15+ years of experience serving clients across 50+ countries worldwide"
                  },
                  {
                    title: "Client-Focused Approach",
                    description: "Your success is our mission. We tailor solutions to your unique needs"
                  },
                  {
                    title: "Innovation Driven",
                    description: "We leverage the latest technologies and best practices in every project"
                  },
                  {
                    title: "Proven Track Record",
                    description: "500+ successful projects and 250+ satisfied clients speak for themselves"
                  },
                  {
                    title: "24/7 Support",
                    description: "Our dedicated team is always available to support your business needs"
                  }
                ].map((item, index) => (
                  <CarouselItem key={index} className="pl-2 basis-full sm:basis-3/4">
                    <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 hover:bg-card hover:border-primary/50 transition-all duration-300 h-full">
                      <CheckCircle className="text-primary mb-4" size={32} />
                      <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">Client Testimonials</h2>
            <p className="text-lg md:text-xl text-muted-foreground">Hear from our satisfied clients</p>
          </div>

          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 4000,
              }),
            ]}
            className="w-full max-w-6xl mx-auto"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-full sm:basis-3/4 md:basis-1/2 lg:basis-1/3">
                  <div className="p-1">
                    <Card className="hover:shadow-xl shadow-primary/20 transition-all duration-300 border-2 hover:border-primary/50 bg-card h-full">
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex mb-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <svg key={i} className="w-5 h-5 text-primary fill-current" viewBox="0 0 20 20">
                              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                            </svg>
                          ))}
                        </div>
                        <p className="text-muted-foreground mb-6 italic flex-grow">"{testimonial.content}"</p>
                        <div>
                          <div className="font-bold text-foreground">{testimonial.name}</div>
                          <div className="text-sm text-muted-foreground">{testimonial.position}</div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 px-4">
            Let's discuss how ALM Group can help you achieve your goals
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50 shadow-primary/50 hover:shadow-primary/80 transition-all duration-300">
                Contact Us Today
              </Button>
            </Link>
            <Link to="/payment">
              <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 shadow-md hover:shadow-lg shadow-primary/50 transition-all duration-300">
                Make a Payment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
